import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Info } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';

interface Booking {
  id: string;
  escort_id: string;
  client_id: string;
  booking_date: string;
  duration: string;
  price: number;
  currency: string;
  status: string;
  notes: string;
  created_at: string;
  profiles?: {
    display_name: string;
  };
}

interface BookingsListProps {
  escortId?: string;
}

const BookingsList: React.FC<BookingsListProps> = ({ escortId }) => {
  const { t, currentLanguage } = useLanguage();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [bookings, setBookings] = useState<Booking[]>([]);

  // Choisir la locale en fonction de la langue actuelle
  const getLocale = () => {
    switch (currentLanguage) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  useEffect(() => {
    if (escortId) {
      loadBookings();
    }
  }, [escortId]);

  const loadBookings = async () => {
    setIsLoading(true);
    try {
      const now = new Date();
      
      // Récupérer les réservations à venir
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          profiles:client_id(display_name)
        `)
        .eq('escort_id', escortId)
        .gte('booking_date', now.toISOString())
        .order('booking_date', { ascending: true });
      
      if (error) throw error;
      
      setBookings(data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast({
        title: t('escort.calendar.error_loading_bookings'),
        description: t('escort.calendar.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status })
        .eq('id', bookingId);
      
      if (error) throw error;
      
      // Mettre à jour la liste locale
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking.id === bookingId ? { ...booking, status } : booking
        )
      );
      
      toast({
        title: t('escort.calendar.booking_updated'),
        description: t('escort.calendar.booking_status_changed', { status }),
      });
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: t('escort.calendar.error_updating_booking'),
        description: t('escort.calendar.try_again_later'),
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-amber-100 text-amber-800">{t('escort.calendar.pending')}</Badge>;
      case 'confirmed':
        return <Badge className="bg-green-500">{t('escort.calendar.confirmed')}</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-red-100 text-red-800">{t('escort.calendar.cancelled')}</Badge>;
      case 'completed':
        return <Badge className="bg-blue-500">{t('escort.calendar.completed')}</Badge>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <p className="text-muted-foreground mb-2">{t('escort.calendar.no_upcoming_bookings')}</p>
        <p className="text-sm">{t('escort.calendar.bookings_appear_here')}</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('escort.calendar.date')}</TableHead>
          <TableHead>{t('escort.calendar.time')}</TableHead>
          <TableHead>{t('escort.calendar.client')}</TableHead>
          <TableHead>{t('escort.calendar.duration')}</TableHead>
          <TableHead>{t('escort.calendar.price')}</TableHead>
          <TableHead>{t('escort.calendar.status')}</TableHead>
          <TableHead className="text-right">{t('escort.calendar.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {bookings.map((booking) => (
          <TableRow key={booking.id}>
            <TableCell>
              {format(new Date(booking.booking_date), 'PP', { locale: getLocale() })}
            </TableCell>
            <TableCell>
              {format(new Date(booking.booking_date), 'HH:mm')}
            </TableCell>
            <TableCell>
              {booking.profiles?.display_name || t('escort.calendar.unknown_client')}
              {booking.notes && (
                 <TooltipProvider delayDuration={100}>
                   <Tooltip>
                     <TooltipTrigger asChild>
                       <Button variant="ghost" size="icon" className="h-6 w-6 ml-1">
                         <Info className="h-4 w-4 text-muted-foreground" />
                       </Button>
                     </TooltipTrigger>
                     <TooltipContent>
                       <p className="max-w-xs">{booking.notes}</p>
                     </TooltipContent>
                   </Tooltip>
                 </TooltipProvider>
              )}
            </TableCell>
            <TableCell>{booking.duration || '-'}</TableCell>
            <TableCell>
              {booking.price 
                ? `${booking.price} ${booking.currency || 'EUR'}`
                : '-'}
            </TableCell>
            <TableCell>{getStatusBadge(booking.status)}</TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                {booking.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                      className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                    >
                      {t('escort.calendar.confirm')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                      className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                    >
                      {t('escort.calendar.cancel')}
                    </Button>
                  </>
                )}
                {booking.status === 'confirmed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateBookingStatus(booking.id, 'completed')}
                  >
                    {t('escort.calendar.mark_completed')}
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default BookingsList;
